export type NotificationType = 'dm' | 'reply' | 'like' | 'takedown';

export interface NotificationRecord {
  id: string;
  type: NotificationType;
  text: string;
  fromUserId?: string;
  fromUsername?: string;
  postId?: string;
  createdAt: number;
  read: boolean;
}

const NOTIFICATIONS_KEY = 'authenticulture_notifications';
const MAX_NOTIFICATIONS = 200;

type NotificationListener = () => void;
let listeners: NotificationListener[] = [];

export function addNotificationsListener(fn: NotificationListener): () => void {
  listeners.push(fn);
  return () => { listeners = listeners.filter(l => l !== fn); };
}

function notify() {
  listeners.forEach(fn => fn());
}

function getAllNotifications(): NotificationRecord[] {
  try {
    return JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveNotifications(notifications: NotificationRecord[]): void {
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(notifications));
}

export function getNotifications(): NotificationRecord[] {
  return getAllNotifications().sort((a, b) => b.createdAt - a.createdAt);
}

export function getUnreadCount(): number {
  return getAllNotifications().filter(n => !n.read).length;
}

export function pushNotification(params: {
  id: string;
  type: NotificationType;
  text: string;
  fromUserId?: string;
  fromUsername?: string;
  postId?: string;
  createdAt?: number;
}): void {
  const notifications = getAllNotifications();
  if (notifications.find(n => n.id === params.id)) return;
  notifications.unshift({
    id: params.id,
    type: params.type,
    text: params.text,
    fromUserId: params.fromUserId,
    fromUsername: params.fromUsername,
    postId: params.postId,
    createdAt: params.createdAt ?? Date.now(),
    read: false,
  });
  if (notifications.length > MAX_NOTIFICATIONS) notifications.splice(MAX_NOTIFICATIONS);
  saveNotifications(notifications);
  notify();
  showBrowserNotification(params.text);
}

export function markRead(id: string): void {
  const notifications = getAllNotifications();
  const record = notifications.find(n => n.id === id);
  if (record && !record.read) {
    record.read = true;
    saveNotifications(notifications);
    notify();
  }
}

export function markAllRead(): void {
  const notifications = getAllNotifications();
  let changed = false;
  for (const n of notifications) {
    if (!n.read) { n.read = true; changed = true; }
  }
  if (changed) {
    saveNotifications(notifications);
    notify();
  }
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.requestPermission();
}

function showBrowserNotification(body: string, title = 'Authenticulture'): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (typeof document !== 'undefined' && !document.hidden) return;
  try {
    new Notification(title, { body, icon: '/logo192.png' });
  } catch {
    // Notification constructor can throw on some platforms (e.g. Android Chrome) — non-fatal
  }
}
